// Static, frontend-only guide content for the Inventory module.
// Bilingual (English / Hindi). No backend call — purely a dictionary.

export type GuideLang = "en" | "hi";

export interface GuideSection {
  key: string;
  title: Record<GuideLang, string>;
  points: Record<GuideLang, string[]>;
}

export const GUIDE_LABELS: Record<GuideLang, { heading: string; intro: string; toggle: string }> = {
  en: {
    heading: "How to manage Inventory",
    intro:
      "Stock is tracked per Location and per Batch. Goods come IN through a Goods Receipt (GRN); sales take stock OUT automatically. Follow the steps below to set things up.",
    toggle: "हिंदी",
  },
  hi: {
    heading: "इन्वेंट्री कैसे प्रबंधित करें",
    intro:
      "स्टॉक हर लोकेशन और हर बैच के अनुसार ट्रैक होता है। माल 'गुड्स रिसीप्ट (GRN)' के ज़रिए अंदर आता है; बिक्री होने पर स्टॉक अपने आप घट जाता है। सेटअप के लिए नीचे दिए चरणों का पालन करें।",
    toggle: "English",
  },
};

export const INVENTORY_GUIDE: GuideSection[] = [
  {
    key: "locations",
    title: { en: "1. Set up Locations", hi: "1. लोकेशन बनाएँ" },
    points: {
      en: [
        "A Location is any place stock lives — warehouse, bar, kitchen, outlet or retail store.",
        "Create at least one Location before receiving stock.",
        "Mark one as the default — sales invoices without a chosen location will deplete from it.",
      ],
      hi: [
        "लोकेशन वह जगह है जहाँ स्टॉक रखा जाता है — वेयरहाउस, बार, किचन, आउटलेट या रिटेल स्टोर।",
        "स्टॉक प्राप्त करने से पहले कम से कम एक लोकेशन बनाएँ।",
        "एक को डिफ़ॉल्ट बनाएँ — जिन बिक्री इनवॉइस में लोकेशन नहीं चुनी गई, उनका स्टॉक इसी से घटेगा।",
      ],
    },
  },
  {
    key: "items",
    title: { en: "2. Configure your Items (SKUs)", hi: "2. आइटम (SKU) कॉन्फ़िगर करें" },
    points: {
      en: [
        "Set the base unit each item is stored in (e.g. ml, gram, piece).",
        "Add unit conversions for how you buy/sell (e.g. 1 bottle = 750 ml, 1 peg = 30 ml).",
        "Turn on batch tracking for perishables and lot-tracked liquor so expiry is followed.",
      ],
      hi: [
        "हर आइटम की बेस यूनिट सेट करें (जैसे ml, ग्राम, पीस)।",
        "खरीद/बिक्री के लिए यूनिट कन्वर्ज़न जोड़ें (जैसे 1 बोतल = 750 ml, 1 पेग = 30 ml)।",
        "जल्दी खराब होने वाले और लॉट-वार शराब के लिए बैच ट्रैकिंग चालू करें ताकि एक्सपायरी ट्रैक हो।",
      ],
    },
  },
  {
    key: "grn",
    title: { en: "3. Receive stock with a GRN", hi: "3. GRN से स्टॉक प्राप्त करें" },
    points: {
      en: [
        "Goods Receipt (GRN) is the only way stock comes in.",
        "For batch-tracked items, enter a batch number, expiry and cost — this creates the batch.",
        "Posting the GRN adds the quantity to that location's stock.",
      ],
      hi: [
        "गुड्स रिसीप्ट (GRN) ही स्टॉक अंदर लाने का एकमात्र तरीका है।",
        "बैच-ट्रैक आइटम के लिए बैच नंबर, एक्सपायरी और लागत भरें — इससे बैच बनता है।",
        "GRN पोस्ट करते ही मात्रा उस लोकेशन के स्टॉक में जुड़ जाती है।",
      ],
    },
  },
  {
    key: "recipes",
    title: { en: "4. Define Recipes (for prepared items)", hi: "4. रेसिपी बनाएँ (तैयार आइटम के लिए)" },
    points: {
      en: [
        "A recipe lists which SKUs (and how much) a sellable item consumes.",
        "Raw liquor / retail goods are a single-component recipe; cocktails and dishes have many.",
        "When that item sells, the recipe explodes and the right stock is depleted.",
      ],
      hi: [
        "रेसिपी बताती है कि कोई बिकने वाला आइटम कौन-से SKU और कितनी मात्रा में खर्च करता है।",
        "कच्ची शराब / रिटेल माल एकल-घटक रेसिपी है; कॉकटेल और व्यंजनों में कई घटक होते हैं।",
        "जब वह आइटम बिकता है, रेसिपी खुलती है और सही स्टॉक घटाया जाता है।",
      ],
    },
  },
  {
    key: "sales",
    title: { en: "5. Sales reduce stock automatically", hi: "5. बिक्री से स्टॉक अपने आप घटता है" },
    points: {
      en: [
        "Creating a sales invoice depletes stock from its location using FEFO (earliest expiry first).",
        "POS sales depvete stock the same way via the sale-event feed.",
        "You never adjust stock by hand for a sale — the ledger does it.",
      ],
      hi: [
        "बिक्री इनवॉइस बनाने पर उसकी लोकेशन से FEFO (पहले एक्सपायर होने वाला पहले) के अनुसार स्टॉक घटता है।",
        "POS बिक्री भी सेल-इवेंट के ज़रिए इसी तरह स्टॉक घटाती है।",
        "बिक्री के लिए स्टॉक हाथ से कभी न बदलें — लेजर यह स्वतः करता है।",
      ],
    },
  },
  {
    key: "transfers",
    title: { en: "6. Move stock between Locations", hi: "6. लोकेशनों के बीच स्टॉक भेजें" },
    points: {
      en: [
        "An outlet raises a Requisition; once approved it becomes a Stock Transfer.",
        "Dispatch reduces the source; Receive adds it to the destination — the same batch carries across.",
        "Between dispatch and receive the goods are 'in transit'.",
      ],
      hi: [
        "आउटलेट एक रिक्विज़िशन बनाता है; मंज़ूरी मिलते ही वह स्टॉक ट्रांसफर बन जाता है।",
        "डिस्पैच से स्रोत घटता है; रिसीव से गंतव्य में जुड़ता है — वही बैच साथ जाता है।",
        "डिस्पैच और रिसीव के बीच माल 'ट्रांज़िट में' रहता है।",
      ],
    },
  },
  {
    key: "containers",
    title: { en: "7. Open Containers (bars)", hi: "7. खुले कंटेनर (बार के लिए)" },
    points: {
      en: [
        "Opening a bottle is a state change — it does NOT reduce stock by itself.",
        "The screen shows how many are sealed vs open, and warns when an opened bottle is near expiry.",
        "Pouring from an open bottle reduces stock as part of the sale.",
      ],
      hi: [
        "बोतल खोलना सिर्फ़ एक स्थिति बदलाव है — इससे स्टॉक अपने आप कम नहीं होता।",
        "स्क्रीन दिखाती है कि कितनी सील्ड हैं और कितनी खुली, और खुली बोतल की एक्सपायरी पास आने पर चेतावनी देती है।",
        "खुली बोतल से सर्व करने पर बिक्री के हिस्से के रूप में स्टॉक घटता है।",
      ],
    },
  },
];
